import React, { useMemo, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Input } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { fetchApi } from '../../../services/api';
import { Error, Hint, Label } from '@progress/kendo-react-labels';
import { FieldWrapper } from '@progress/kendo-react-form';

interface CreateLocationFormData {
  Name: string;
}

interface CreateLocationDialogProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreateLocationDialog: React.FC<CreateLocationDialogProps> = ({
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const canSave = useMemo(() => name.trim().length > 0 && !loading, [name, loading]);

  const handleCancel = () => {
    if (loading) {
      return;
    }

    setName('');
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Location name is required');
      return;
    }

    setLoading(true);
    setError(null);

    let shouldClose = false;

    try {
      await fetchApi('/api/ShopInventory/CreateLocation', { Name: name.trim() }, 'POST');
      setName('');
      onSuccess();
      shouldClose = true;
    } catch (e: any) {
      if (!e?.status) alert('Failed to create location');
    } finally {
      setLoading(false);
      if (shouldClose) {
        onClose();
      }
    }
  };

  return (
    <Dialog
      title="Create New Location"
      onClose={handleCancel}
      width={400}
      height={300}
    >
      <FieldWrapper>
        <Label>Location Name</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.value ?? '')}
          placeholder="e.g., Shop Office, Tire Rack 3, Workshop Bay 7"
          disabled={loading}
        />
        {error && <Error>{error}</Error>}
      </FieldWrapper>
      <DialogActionsBar>
        <Button
          type="button"
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="button"
          themeColor="primary"
          disabled={!canSave}
          onClick={handleSave}
        >
          {loading ? 'Creating...' : 'Create Location'}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default CreateLocationDialog;
